import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric9,
  type DashboardMetric9ControlsEffectiveness,
} from '@/services/dashboardService';
import { EffectivenessStatus } from '@/services/controlActivitiesService';

const { Text } = Typography;

type DonutDatum = {
  key: EffectivenessStatus;
  label: string;
  count: number;
  percent: number; // 0..100
};

const COLORS: Record<EffectivenessStatus, string> = {
  effective: '#22C55E', // green
  ineffective: '#EF4444', // red
  untested: '#E6A700', // gold/yellow
  outofscope: '#9CA3AF', // grey
};

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const Metric9Tooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d: DonutDatum | undefined = payload?.[0]?.payload;
  if (!d) return null;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 240,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{d.label}</div>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
      >
        <span>Count</span>
        <span style={{ fontWeight: 700 }}>{d.count}</span>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          marginTop: 4,
        }}
      >
        <span>Percent</span>
        <span style={{ fontWeight: 700 }}>{formatPercent(d.percent)}</span>
      </div>
      <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
        Click slice to open Exhibit 5
      </div>
    </div>
  );
};

const renderCenterLabel =
  (percentEffective: number) =>
  ({ viewBox }: any) => {
    const { cx, cy } = viewBox || {};
    if (cx == null || cy == null) return null;

    return (
      <g>
        <text
          x={cx}
          y={cy}
          textAnchor='middle'
          dominantBaseline='middle'
          style={{ fontSize: 18, fontWeight: 800, fill: '#111827' }}
        >
          {formatPercent(percentEffective)}
        </text>
        <text
          x={cx}
          y={cy + 18}
          textAnchor='middle'
          dominantBaseline='middle'
          style={{ fontSize: 12, fontWeight: 700, fill: 'rgba(0,0,0,0.65)' }}
        >
          Effective
        </text>
      </g>
    );
  };

export type EX1TotalControlEffectivenessDonutChartProps = {
  height?: number;
  title?: string;
};

export const EX1TotalControlEffectivenessDonutChart: React.FC<
  EX1TotalControlEffectivenessDonutChartProps
> = ({
  height = 360,
  title = 'Total Effective vs Ineffective vs Untested Controls',
}) => {
  const navigate = useNavigate();

  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<DashboardMetric9ControlsEffectiveness | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw(null);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric9(annualReportId);
        if (!isMounted) return;
        setRaw(data);
      } catch (err: any) {
        if (!isMounted) return;
        setRaw(null);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 9 (Control Effectiveness breakdown).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const total = useMemo(() => {
    if (!raw) return 0;
    return (
      (raw.effectiveControls || 0) +
      (raw.ineffectiveControls || 0) +
      (raw.untestedControls || 0) +
      (raw.outOfScopeControls || 0)
    );
  }, [raw]);

  const percentEffective = useMemo(() => {
    if (!raw || total === 0) return 0;
    return (raw.effectiveControls / total) * 100;
  }, [raw, total]);

  const data: DonutDatum[] = useMemo(() => {
    const eff = raw?.effectiveControls ?? 0;
    const ineff = raw?.ineffectiveControls ?? 0;
    const untested = raw?.untestedControls ?? 0;
    const outofscope = raw?.outOfScopeControls ?? 0;

    const t = total || 0;

    const list: DonutDatum[] = [
      {
        key: 'effective',
        label: 'Effective',
        count: eff,
        percent: t ? (eff / t) * 100 : 0,
      },
      {
        key: 'ineffective',
        label: 'Ineffective',
        count: ineff,
        percent: t ? (ineff / t) * 100 : 0,
      },
      {
        key: 'untested',
        label: 'Untested',
        count: untested,
        percent: t ? (untested / t) * 100 : 0,
      },
      {
        key: 'outofscope',
        label: 'Out Of Scope',
        count: outofscope,
        percent: t ? (outofscope / t) * 100 : 0,
      },
    ];

    // keep slices stable (Effective, Ineffective, Untested), but hide all-zero slices
    return list.filter(x => x.count > 0);
  }, [raw, total]);

  const isEmpty = !loading && !errorMessage && raw && total === 0;

  const goToExhibit5ByEffectiveness = (key: EffectivenessStatus) => {
    navigate('/control-activities', {
      state: {
        effectivenessStatus: key,
      },
    });
  };

  return (
    <Card title={title}>
      <div style={{ height }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 9 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No controls were returned for this report.'
            showIcon
          />
        ) : !raw ? (
          <Alert
            type='info'
            message='Select a report'
            description='Please select an Annual Report to view this metric.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <PieChart
              margin={
                isNarrow
                  ? { top: 8, right: 8, bottom: 24, left: 8 }
                  : { top: 8, right: 160, bottom: 8, left: 8 }
              }
            >
              <Tooltip content={<Metric9Tooltip />} />

              <Legend
                layout={isNarrow ? 'horizontal' : 'vertical'}
                align={isNarrow ? 'center' : 'right'}
                verticalAlign={isNarrow ? 'bottom' : 'middle'}
                wrapperStyle={
                  isNarrow ? { paddingTop: 12 } : { paddingLeft: 16 }
                }
                formatter={(value: any, entry: any) => {
                  const p: DonutDatum | undefined = entry?.payload;
                  if (!p) return value;
                  return `${p.label}: ${p.count} (${formatPercent(p.percent)})`;
                }}
              />

              <Pie
                data={data}
                dataKey='count'
                nameKey='label'
                cx={isNarrow ? '50%' : '42%'}
                cy='50%'
                innerRadius={80}
                outerRadius={120}
                paddingAngle={1}
                isAnimationActive={false}
                onClick={(_, idx) => {
                  const d = data[idx];
                  if (d?.key) goToExhibit5ByEffectiveness(d.key);
                }}
                style={{ cursor: 'pointer' }}
              >
                {data.map(d => (
                  <Cell key={d.key} fill={COLORS[d.key]} />
                ))}

                <Label position="center" content={renderCenterLabel(percentEffective)} />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {!loading && !errorMessage && raw ? (
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <Text type='secondary'>Active Controls Only</Text>
        </div>
      ) : null}
    </Card>
  );
};

export default EX1TotalControlEffectivenessDonutChart;
